import { useRef, useState } from 'react';
import type { CrearPuerta, MaterialPuerta, Puerta, RespuestaPuerta } from '@matias-asset-os/contracts';
import { MATERIALES_PUERTA, RESPUESTAS_PUERTA } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from '../componentes/Iconos.js';

/**
 * REGISTRAR UNA PUERTA DESDE LA ACERA.
 *
 * Esta pantalla tiene un solo objetivo y todo lo demás se le subordina: que un
 * captador registre un portal en menos de cuarenta segundos y siga andando. No
 * es «el formulario de alta en móvil»; es la herramienta de alguien que está de
 * pie, con una mano ocupada, con prisa y con treinta timbres por delante.
 *
 * De ahí las cuatro decisiones que la gobiernan:
 *
 *   1. SE TOCA, NO SE ESCRIBE. La dirección sale del GPS; la respuesta y el
 *      material se eligen de una rejilla de botones grandes. El único teclado
 *      que aparece es el del nombre, que casi siempre se deja vacío.
 *   2. LO QUE NO SE PREGUNTA NO EXISTE. Si no atendieron, la mitad de la
 *      pantalla desaparece: no hay respuesta que registrar ni nombre que pedir.
 *   3. NO HAY CONFIRMACIONES. Guardar guarda y deja la pantalla lista para la
 *      siguiente, con el municipio y la zona puestos —el portal de al lado está
 *      en la misma calle— y el número borrado.
 *   4. LA FOTO ES OPCIONAL Y VA LA ÚLTIMA. Es lo que más tarda; obligarla
 *      convertiría cuarenta segundos en dos minutos.
 */

interface Props {
  idioma: Idioma;
  onCerrar: () => void;
  /** Para que la lista y el mapa de detrás se enteren sin recargar la página. */
  onRegistrada: (puerta: Puerta) => void;
}

type Paso = 'formulario' | 'guardada';

export function CargaRapida({ idioma, onCerrar, onRegistrada }: Props) {
  const [direccion, setDireccion] = useState('');
  const [codigoPostal, setCodigoPostal] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [zona, setZona] = useState('');
  const [punto, setPunto] = useState<{ lat: number; lon: number } | null>(null);
  const [atendio, setAtendio] = useState<boolean | null>(null);
  const [respuesta, setRespuesta] = useState<RespuestaPuerta | null>(null);
  const [respuestaTexto, setRespuestaTexto] = useState('');
  const [material, setMaterial] = useState<MaterialPuerta>('NINGUNO');
  const [contacto, setContacto] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);

  const [ubicando, setUbicando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paso, setPaso] = useState<Paso>('formulario');
  const [ultima, setUltima] = useState<string>('');
  /* Cuantas fotos quedaron colgadas de la ficha, y cuantas se quedaron por el camino. */
  const [fotosGuardadas, setFotosGuardadas] = useState(0);
  const [fotosPerdidas, setFotosPerdidas] = useState(0);
  const campoDireccion = useRef<HTMLInputElement>(null);

  /**
   * «Estoy aquí».
   *
   * El móvil da el punto y el servidor devuelve la calle con su número. Si el
   * GPS falla —dentro de un patio, en una calle estrecha— no se bloquea nada:
   * queda el campo de texto, que es lo que había antes de existir este botón.
   */
  async function ubicar(): Promise<void> {
    if (!('geolocation' in navigator)) {
      setError(t(idioma, 'pap.sinGps'));
      return;
    }
    setUbicando(true);
    setError(null);
    try {
      const pos = await new Promise<GeolocationPosition>((ok, mal) =>
        navigator.geolocation.getCurrentPosition(ok, mal, {
          enableHighAccuracy: true,
          timeout: 12_000,
          maximumAge: 15_000,
        }),
      );
      const lat = pos.coords.latitude;
      const lon = pos.coords.longitude;
      setPunto({ lat, lon });
      const { lugar } = await api.lugarAqui(lat, lon);
      if (lugar) {
        // La etiqueta de Nominatim trae el país y la provincia detrás; lo que
        // identifica un portal son las dos primeras partes.
        setDireccion(lugar.etiqueta.split(',').slice(0, 2).join(',').trim());
        if (lugar.municipio) setMunicipio(lugar.municipio);
        if (lugar.zona) setZona(lugar.zona);
        if (lugar.codigoPostal) setCodigoPostal(lugar.codigoPostal);
      }
    } catch {
      setError(t(idioma, 'pap.noSePudoUbicar'));
    } finally {
      setUbicando(false);
    }
  }

  const listo = direccion.trim().length >= 3 && atendio !== null;

  async function guardar(): Promise<void> {
    if (!listo) return;
    setGuardando(true);
    setError(null);
    try {
      const datos: CrearPuerta = {
        direccion: direccion.trim(),
        atendio: atendio === true,
        ...(codigoPostal ? { codigoPostal } : {}),
        ...(municipio ? { municipio } : {}),
        ...(zona ? { zona } : {}),
        ...(punto ? { lat: punto.lat, lon: punto.lon } : {}),
        ...(atendio && respuesta ? { respuesta } : {}),
        ...(atendio && respuestaTexto.trim() ? { respuestaTexto: respuestaTexto.trim() } : {}),
        material,
        ...(atendio && contacto.trim() ? { contactoNombre: contacto.trim() } : {}),
      };
      const { puerta } = await api.registrarPuerta(datos);

      /*
        Las fotos van DESPUES del alta: la ficha ya existe y es a ella a quien
        cuelgan. Si una falla, la puerta queda registrada igual —perder el
        portal entero por una foto seria peor— pero SE DICE. Antes el fallo se
        tragaba en silencio y la foto desaparecia sin que nadie se enterara.
      */
      let fallidas = 0;
      for (const fichero of fotos) {
        try {
          await api.subirAdjunto('PUERTA', puerta.id, fichero);
        } catch {
          fallidas += 1;
        }
      }
      const final = fotos.length > 0 ? (await api.puerta(puerta.id)).puerta : puerta;
      onRegistrada(final);
      setUltima(final.direccion);
      setFotosGuardadas(final.fotos.length);
      setFotosPerdidas(fallidas);
      setPaso('guardada');
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setGuardando(false);
    }
  }

  /**
   * La siguiente puerta está al lado de esta.
   *
   * Se conserva el municipio, la zona y el código postal —la calle es la misma—
   * y se borra todo lo que cambia de portal a portal. Volver a pedir la
   * ubicación es un toque, no un formulario.
   */
  function siguiente(): void {
    setDireccion('');
    setPunto(null);
    setAtendio(null);
    setRespuesta(null);
    setRespuestaTexto('');
    setMaterial('NINGUNO');
    setContacto('');
    setFotos([]);
    setFotosGuardadas(0);
    setFotosPerdidas(0);
    setError(null);
    setPaso('formulario');
    setTimeout(() => campoDireccion.current?.focus(), 50);
  }

  if (paso === 'guardada') {
    return (
      <div className="pap-carga">
        <div className="pap-guardada">
          <Icono nombre="comprobado" />
          <h2>{t(idioma, 'pap.guardada')}</h2>
          <p className="suave">{ultima}</p>
          {/*
            CUANTAS FOTOS QUEDARON. Se dice siempre que hubo alguna: es lo unico
            que distingue «la foto esta en la ficha» de «la foto se perdio», y
            en la calle no hay forma de comprobarlo despues.
          */}
          {fotosGuardadas > 0 && (
            <p className="suave">{t(idioma, 'pap.fotosGuardadas', { total: fotosGuardadas })}</p>
          )}
          {fotosPerdidas > 0 && (
            <p className="error" role="alert">
              {t(idioma, 'pap.fotosPerdidas', { total: fotosPerdidas })}
            </p>
          )}
          <button type="button" className="pap-boton-grande" onClick={siguiente}>
            <Icono nombre="mas" /> {t(idioma, 'pap.siguiente')}
          </button>
          <button type="button" className="enlace" onClick={onCerrar}>
            {t(idioma, 'pap.terminar')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pap-carga">
      <div className="pap-cabeza">
        <button type="button" className="enlace" onClick={onCerrar}>
          <Icono nombre="volver" /> {t(idioma, 'app.volver')}
        </button>
        <b>{t(idioma, 'pap.nuevaPuerta')}</b>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* 1 · DÓNDE. Lo único obligatorio, y sale de un botón. */}
      <section className="pap-bloque">
        <button
          type="button"
          className="pap-boton-grande"
          disabled={ubicando}
          onClick={() => void ubicar()}
        >
          <Icono nombre="pin" /> {t(idioma, ubicando ? 'pap.ubicando' : 'pap.estoyAqui')}
        </button>
        <input
          ref={campoDireccion}
          type="text"
          className="pap-direccion"
          value={direccion}
          placeholder={t(idioma, 'pap.direccion')}
          onChange={(e) => setDireccion(e.target.value)}
          aria-label={t(idioma, 'pap.direccion')}
        />
        {(municipio || zona) && (
          <p className="suave pap-zona">{[zona, municipio, codigoPostal].filter(Boolean).join(' · ')}</p>
        )}
      </section>

      {/* 2 · ¿NOS ATENDIERON? Dos botones del tamaño de un pulgar. */}
      <section className="pap-bloque">
        <h3>{t(idioma, 'pap.atendieron')}</h3>
        <div className="pap-dos">
          <button
            type="button"
            className={`pap-opcion${atendio === true ? ' elegida' : ''}`}
            aria-pressed={atendio === true}
            onClick={() => setAtendio(true)}
          >
            {t(idioma, 'app.si')}
          </button>
          <button
            type="button"
            className={`pap-opcion${atendio === false ? ' elegida' : ''}`}
            aria-pressed={atendio === false}
            onClick={() => {
              setAtendio(false);
              setRespuesta(null);
              setRespuestaTexto('');
              setContacto('');
            }}
          >
            {t(idioma, 'app.no')}
          </button>
        </div>
      </section>

      {/* 3 · QUÉ DIJERON. Sólo si abrieron: lo que no se pregunta no existe. */}
      {atendio === true && (
        <>
          <section className="pap-bloque">
            <h3>{t(idioma, 'pap.queDijeron')}</h3>
            <div className="pap-rejilla">
              {RESPUESTAS_PUERTA.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`pap-opcion${respuesta === r ? ' elegida' : ''}`}
                  aria-pressed={respuesta === r}
                  onClick={() => setRespuesta(respuesta === r ? null : r)}
                >
                  {t(idioma, `pap.respuesta.${r}`)}
                </button>
              ))}
            </div>
            {respuesta === 'OTRA' && (
              <textarea
                className="pap-texto"
                rows={2}
                value={respuestaTexto}
                placeholder={t(idioma, 'pap.respuestaOtra')}
                onChange={(e) => setRespuestaTexto(e.target.value)}
                aria-label={t(idioma, 'pap.respuestaOtra')}
              />
            )}
          </section>

          <section className="pap-bloque">
            <h3>{t(idioma, 'pap.contacto')}</h3>
            <input
              type="text"
              className="pap-direccion"
              value={contacto}
              placeholder={t(idioma, 'pap.contactoPie')}
              onChange={(e) => setContacto(e.target.value)}
              aria-label={t(idioma, 'pap.contacto')}
            />
          </section>
        </>
      )}

      {/* 4 · QUÉ DEJAMOS. */}
      <section className="pap-bloque">
        <h3>{t(idioma, 'pap.material')}</h3>
        <div className="pap-rejilla">
          {MATERIALES_PUERTA.map((m) => (
            <button
              key={m}
              type="button"
              className={`pap-opcion${material === m ? ' elegida' : ''}`}
              aria-pressed={material === m}
              onClick={() => setMaterial(m)}
            >
              {t(idioma, `pap.material.${m}`)}
            </button>
          ))}
        </div>
      </section>

      {/* 5 · LAS FOTOS. La última, porque es lo que más tarda. */}
      <section className="pap-bloque">
        <h3>{t(idioma, 'pap.fotos')}</h3>
        <p className="suave">{t(idioma, 'pap.fotosPie')}</p>
        <label className="pap-boton-grande secundario">
          <Icono nombre="camara" /> {t(idioma, 'pap.hacerFoto')}
          <input
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              /*
                LAS FOTOS SE COGEN AHORA, NO DESPUES.

                Antes se leian dentro de la funcion que actualiza el estado, y
                esa funcion React la ejecuta MAS TARDE. Para entonces ya se
                habia vaciado el campo —la linea de abajo, que esta ahi para
                poder elegir dos veces la misma foto— y lo que se leia era una
                lista vacia. La foto se hacia, se decia que estaba lista y no
                llegaba a ninguna parte.
              */
              const elegidas = [...(e.target.files ?? [])];
              e.target.value = '';
              if (elegidas.length > 0) setFotos((p) => [...p, ...elegidas]);
            }}
          />
        </label>
        {fotos.length > 0 && (
          <p className="suave">{t(idioma, 'pap.fotosElegidas', { total: fotos.length })}</p>
        )}
      </section>

      {/* 6 · GUARDAR. Pegado abajo: se llega con el pulgar sin mirar. */}
      <div className="pap-pie">
        <button
          type="button"
          className="pap-boton-grande pap-guardar"
          disabled={!listo || guardando}
          onClick={() => void guardar()}
        >
          {t(idioma, guardando ? 'pap.guardando' : 'pap.guardar')}
        </button>
      </div>
    </div>
  );
}
